import { useState } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { NHAN_KIEU_TRUONG } from '../../constants-bot';
import {
  KICH_BAN_RONG,
  KIEU_TRUONG,
  chuanHoaKichBan,
  doiCho,
  gopBanKhaiCu,
} from '../../nghiep-vu/cau-hinh-bot';
import { docBuoc, type BuocHoiThoai, type KieuTruong, type Scenario } from '../../types';

/**
 * Soạn một kịch bản hội thoại (port `components/KichBan.tsx` native).
 *
 * Không phải ba ô (tên · khi nào · trả lời gì): "Mở thẻ tín dụng" là một cuộc hội thoại có thứ tự, có thông tin phải thu
 * thập trước khi đi tiếp, và có điều cấm. Hai danh sách: các bước (THỨ TỰ là nội dung — đánh số, đổi chỗ được; bước nào
 * cần lấy thông tin thì tích "Thu thập" ngay trên bước ấy) và quy tắc (đúng ở MỌI bước, nên không đánh số).
 *
 * Đổi thứ tự bằng nút, không kéo thả: kéo thả không dùng được bằng bàn phím — mà đây là màn người ta soạn bằng bàn phím.
 */

function NutDoiCho({
  i, tong, tat, onDoi,
}: {
  i: number;
  tong: number;
  tat?: boolean;
  onDoi: (a: number, b: number) => void;
}) {
  const { t, locale } = useT();
  if (tong < 2) return null;
  return (
    <div className="flex shrink-0">
      <Button
        variant="ghost"
        size="sm"
        disabled={tat || i === 0}
        aria-label={t('troLyAiBotKichBan.duaLen', { i: soVN(i + 1, locale) })}
        icon={<ArrowUpOutlined />}
        onClick={() => onDoi(i - 1, i)}
      />
      <Button
        variant="ghost"
        size="sm"
        disabled={tat || i === tong - 1}
        aria-label={t('troLyAiBotKichBan.duaXuong', { i: soVN(i + 1, locale) })}
        icon={<ArrowDownOutlined />}
        onClick={() => onDoi(i, i + 1)}
      />
    </div>
  );
}

function SoThuTu({ i }: { i: number }) {
  const { locale } = useT();
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-subtle text-xs text-content-secondary">
      {soVN(i + 1, locale)}
    </span>
  );
}

/**
 * Các bước hội thoại — MỘT danh sách, MỘT thứ tự. Bản cũ tách "thông tin bắt buộc" khỏi "các bước", nên có hai thứ tự:
 * bản chạy hỏi theo danh sách bắt buộc, người soạn đọc danh sách bước. Ô chọn kiểu chỉ hiện khi đã tích Thu thập — kiểu
 * chỉ có nghĩa cho bước thu thập.
 */
function DanhSachBuoc({
  giaTri, onDoi, disabled,
}: {
  giaTri: (string | BuocHoiThoai)[];
  onDoi: (v: BuocHoiThoai[]) => void;
  disabled?: boolean;
}) {
  const { t, locale } = useT();
  const ds = giaTri.map(docBuoc);
  const sua = (i: number, phan: Partial<BuocHoiThoai>) => onDoi(ds.map((x, j) => (j === i ? { ...x, ...phan } : x)));

  return (
    <div>
      <p className="text-xs font-medium text-content-primary">{t('troLyAiBotKichBan.cacBuoc')}</p>
      <p className="mb-1.5 text-xs leading-normal text-content-muted">{t('troLyAiBotKichBan.cacBuocGhi')}</p>
      <div className="space-y-1.5">
        {ds.map((x, i) => (
          <div key={i} className="rounded border border-default p-1.5">
            <div className="flex items-center gap-1.5">
              <SoThuTu i={i} />
              <Input
                size="small"
                disabled={disabled}
                placeholder={t('troLyAiBotKichBan.viDuBuoc')}
                value={x.ten}
                aria-label={t('troLyAiBotKichBan.buocSo', { i: soVN(i + 1, locale) })}
                onChange={(e) => sua(i, { ten: e.target.value })}
              />
              <NutDoiCho i={i} tong={ds.length} tat={disabled} onDoi={(a, b) => onDoi(doiCho(ds, a, b))} />
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={t('troLyAiBotKichBan.xoaBuoc', { i: soVN(i + 1, locale) })}
                icon={<DeleteOutlined />}
                onClick={() => onDoi(ds.filter((_, j) => j !== i))}
              />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 ps-6">
              <Checkbox
                disabled={disabled}
                checked={x.thuThap}
                aria-label={t('troLyAiBotKichBan.thuThapCua', { i: soVN(i + 1, locale) })}
                onChange={(e) => sua(i, { thuThap: e.target.checked })}
              >
                <span className="text-xs font-medium text-content-primary">{t('troLyAiBotKichBan.thuThap')}</span>
                <span className="text-xs text-content-muted"> — {t('troLyAiBotKichBan.thuThapGhi')}</span>
              </Checkbox>
              {x.thuThap ? (
                <Select<KieuTruong>
                  size="small"
                  disabled={disabled}
                  className="ms-auto w-[150px] shrink-0"
                  aria-label={t('troLyAiBotKichBan.kieuCua', { ten: x.ten || soVN(i + 1, locale) })}
                  value={x.kieu}
                  onChange={(v) => sua(i, { kieu: v })}
                  options={KIEU_TRUONG.map((k) => ({ value: k, label: t(NHAN_KIEU_TRUONG[k]) }))}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled}
        className="mt-1"
        icon={<PlusOutlined />}
        onClick={() => onDoi([...ds, { ten: '', thuThap: false, kieu: 'van_ban' }])}
      >
        {t('troLyAiBotKichBan.themBuoc')}
      </Button>
    </div>
  );
}

function DanhSachQuyTac({
  giaTri, onDoi, disabled,
}: {
  giaTri: string[];
  onDoi: (v: string[]) => void;
  disabled?: boolean;
}) {
  const { t, locale } = useT();
  return (
    <div>
      <p className="text-xs font-medium text-content-primary">{t('troLyAiBotKichBan.quyTac')}</p>
      <p className="mb-1.5 text-xs leading-normal text-content-muted">{t('troLyAiBotKichBan.quyTacGhi')}</p>
      <div className="space-y-1.5">
        {giaTri.map((x, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input
              size="small"
              disabled={disabled}
              placeholder={t('troLyAiBotKichBan.quyTacViDu')}
              value={x}
              aria-label={t('troLyAiBotKichBan.quyTacSo', { i: soVN(i + 1, locale) })}
              onChange={(e) => onDoi(giaTri.map((y, j) => (j === i ? e.target.value : y)))}
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              aria-label={t('troLyAiBotKichBan.xoaQuyTac', { i: soVN(i + 1, locale) })}
              icon={<DeleteOutlined />}
              onClick={() => onDoi(giaTri.filter((_, j) => j !== i))}
            />
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled}
        className="mt-1"
        icon={<PlusOutlined />}
        onClick={() => onDoi([...giaTri, ''])}
      >
        {t('troLyAiBotKichBan.themQuyTac')}
      </Button>
    </div>
  );
}

export function KichBanDialog({
  open, banDau, onClose, onLuu, disabled,
}: {
  open: boolean;
  /** `null` = thêm mới. */
  banDau: Scenario | null;
  onClose: () => void;
  onLuu: (k: Scenario) => void;
  disabled?: boolean;
}) {
  const { t, locale } = useT();
  // Gộp NGAY LÚC MỞ: người soạn thấy một danh sách, kể cả với bản khai cũ hai danh sách.
  const [f, setF] = useState<Scenario>(() => (banDau ? { ...banDau, steps: gopBanKhaiCu(banDau) } : KICH_BAN_RONG));
  const soCu = banDau?.requiredFields?.length ?? 0;
  const dat = <K extends keyof Scenario>(k: K, v: Scenario[K]) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} width={680} title={banDau ? t('troLyAiBotKichBan.sua') : t('troLyAiBotKichBan.them')}>
      <div className="space-y-4 py-2">
        <Field label={t('troLyAiBotKichBan.ten')} required>
          {(p) => (
            <Input
              {...p}
              disabled={disabled}
              placeholder={t('troLyAiBotViDu.kbTen')}
              value={f.name}
              onChange={(e) => dat('name', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('troLyAiBotKichBan.moTaNgan')} hint={t('troLyAiBotKichBan.moTaNganGhi')}>
          {(p) => (
            <Input
              {...p}
              disabled={disabled}
              placeholder={t('troLyAiBotViDu.kbTrigger')}
              value={f.trigger}
              onChange={(e) => dat('trigger', e.target.value)}
            />
          )}
        </Field>

        {soCu > 0 ? (
          <p className="rounded bg-subtle px-3 py-2 text-xs leading-relaxed text-content-secondary">
            {t('troLyAiBotKichBan.gopTuBanKhaiCu', { n: soVN(soCu, locale) })}
          </p>
        ) : null}

        <DanhSachBuoc giaTri={f.steps} disabled={disabled} onDoi={(v) => dat('steps', v)} />
        <DanhSachQuyTac giaTri={f.rules} disabled={disabled} onDoi={(v) => dat('rules', v)} />
      </div>
      <div className="flex flex-wrap justify-end gap-2 pt-2">
        <Button variant="outline" size="md" onClick={onClose}>
          {t('chung.huy')}
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={disabled || !f.name.trim()}
          onClick={() => onLuu(chuanHoaKichBan(f))}
        >
          {t('troLyAiBotKichBan.luu')}
        </Button>
      </div>
    </Dialog>
  );
}
